// The generic drill-down stacked horizontal bar chart's surface. Schema-agnostic by
// rule: no project SQL, no table names, no store — everything domain-specific arrives
// through the function props below.
//
//   props:  title            (String)        rendered as-is; already translated
//           chartFontColor   (String)        axis label colour; a change reloads
//           chartCardHeight  (Number)        the card's max height (it scrolls inside
//                                            beyond it); the chart sizes itself to the bar count
//           currentLevel     (String|Number) supports v-model:currentLevel
//           dataProviders    (Object)        { [level]: (filtered = true) => Promise<Row[]> }
//           contextUpdaters  (Object)        { [level]: (id, parentId, type) => Promise<void>, reset }
//           navigationConfig (Array)         ordered [{ level, titleKey }]
//           filterContext    (Object)        the filter-context duck type
//           dataLoaded       (Boolean)       gates the initial load
//           skipEmptyLevels  (Boolean)       opt-in: the top of the chart is the first
//                                            navigation level with data, and a drill click
//                                            that lands on an empty level moves on to the
//                                            next level with data (issue-analysis)
//           labelWidth       (Number)        opt-in: the y-axis label truncation width in px
//                                            (default 120 — asset-management passes 160)
//           valueFormatter   (Function)      opt-in: (value) => string for the x-axis labels,
//                                            the bar labels and the tooltip counts (default
//                                            none — the raw numbers)
//           labelMinWidth    (Number)        opt-in: a bar label only on a segment wider than
//                                            this (px), no "value > 100" shortcut (default null
//                                            — the old rule; asset-management's cost drill: 65)
//           exportFilename   (String)        opt-in: a download button in the title row that saves
//                                            the chart as a PNG under this name ('' = no button)
//           exportTitle      (String)        the PNG's title; '' = `title`
//   emits:  update:currentLevel
//   setup:  downloadPng()  the button's action, callable through a template ref (no-op without
//                          `exportFilename`)
//
// Full contract (row shape, navigationConfig, the load lifecycle, level skipping):
// ../../../README.md. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below — both
// are what the template's `<v-chart>` needs — so it lives here and ./setup.js stays
// pure logic.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { TooltipComponent, LegendComponent, TitleComponent, GridComponent } from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([CanvasRenderer, BarChart, TooltipComponent, LegendComponent, TitleComponent, GridComponent]);

export default {
  name: 'stacked-horizontal-bar-chart',
  props: {
    title: {
      type: String,
      default: ''
    },
    chartFontColor: {
      type: String,
      default: '#000000'
    },
    chartCardHeight: {
      type: Number,
      default: 460
    },
    currentLevel: {
      type: [String, Number],
      default: 0
    },
    dataProviders: {
      type: Object,
      required: true
    },
    contextUpdaters: {
      type: Object,
      required: true
    },
    navigationConfig: {
      type: Array,
    },
    filterContext: {
        type: Object,
        required:true
    },
    dataLoaded: {
      type: Boolean,
      default: false
    },
    // Opt-in (IA_ABSORPTION_PLAN Phase 4 step 12a, upstreamed from issue-analysis's base): at
    // the top of the hierarchy, skip leading navigation levels whose data is empty (e.g. a
    // project that never fills its first level's column); since 2026-10-13 also mid-drill —
    // a click that enters an empty level shows the next level down with data. Off = the entry
    // level is always navigationConfig[0] and every level renders as entered
    // (project-overview's behaviour, unchanged).
    skipEmptyLevels: {
      type: Boolean,
      default: false
    },
    // Opt-in (AM_ABSORPTION_PLAN step C3, from asset-management's base): the y-axis label
    // truncation width in px. The default is the width every chart had (120); asset-management's
    // category and drawing charts pass 160 for their longer leaf labels (asset ids, rooms).
    labelWidth: {
      type: Number,
      default: 120
    },
    // Opt-in (AM_ABSORPTION_PLAN step C3, from asset-management's base): `(value) => string`,
    // applied to the x-axis labels, the bar labels and the tooltip's per-status counts and total
    // (a money chart passes a currency formatter). null = the raw numbers, exactly as before.
    valueFormatter: {
      type: Function,
      default: null
    },
    // Opt-in (AM_ABSORPTION_PLAN step E2, from asset-management's base): a bar segment's label
    // shows only when the segment is wider than this many px (the grid's estimate), with no
    // "value > 100 always shows" shortcut — a money chart's values are all > 100, so without it
    // every narrow bar would carry an overflowing label. null = the rule every chart always had
    // (value > 100, else wider than 45 px).
    labelMinWidth: {
      type: Number,
      default: null
    },
    // Opt-in (2026-10-16, PNG export on every chart): a download button after the breadcrumbs that
    // saves the chart through @rdrive/helper-chart-export — the card title as the PNG's title, the
    // breadcrumb trail (when drilled) as its subtitle, one legend entry per status colour of the
    // page on screen. '' = no button, the title row exactly as before.
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title; '' = `title`
    exportTitle: {
      type: String,
      default: ''
    }
  },
  // Keep the parent's currentLevel binding in sync with internal navigation, so
  // parents that short-circuit levels (by writing the bound ref) always see a change
  emits: ['update:currentLevel'],
  // `<v-chart>` was auto-registered from the import by `<script setup>`; a classic
  // component has to register it explicitly or Vue logs "Failed to resolve component:
  // v-chart" and renders nothing. Vuetify tags need no entry — vite-plugin-vuetify
  // rewrites the compiled render code, not the script block.
  components: { VChart },
  setup,
};
